import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { Mountain, Heart, Target, Users, ArrowRight, Compass, Flag } from "lucide-react";
import { ogImageFor, ogMetaTags } from "@/lib/ogImages";

const SITE_URL = "https://coloradowebimpressions.com";
const OG_IMAGE = `${SITE_URL}${ogImageFor({})}`;

export const Route = createFileRoute("/about")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
        { name: "description", content: "Family-owned, faith-based Colorado Springs digital marketing agency. Owner-led web design, SEO, and PPC for Colorado businesses since 2006." },
      { title: "About | Colorado Web Impressions — CO Marketing Agency" },
      {
        name: "description",
        content:
          "Family-owned, faith-based Colorado digital marketing agency obsessed with growing local service businesses across the Front Range.",
      },
      { property: "og:title", content: "About | Colorado Web Impressions — CO Marketing Agency" },
      {
        property: "og:description",
        content: "A Colorado Springs digital marketing agency built to grow local businesses.",
      },
      { property: "og:url", content: `${SITE_URL}/about` },
      { property: "og:type", content: "website" },
      ...ogMetaTags(OG_IMAGE),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/about` }],
    scripts: [
      // AboutPage schema — links the about page to the canonical Org entity
      // and to Chris's Person node so crawlers attribute the team to the
      // same business defined in __root.tsx.
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": `${SITE_URL}/about#webpage`,
          url: `${SITE_URL}/about`,
          name: "About Colorado Web Impressions",
          description:
            "Colorado-only digital marketing agency built to grow local service businesses across the Front Range.",
          inLanguage: "en-US",
          isPartOf: { "@id": `${SITE_URL}/#org` },
          about: { "@id": `${SITE_URL}/#organization` },
          mainEntity: {
            "@type": "Person",
            "@id": `${SITE_URL}/meet-chris#chris-heidlebaugh`,
            name: "Chris Heidlebaugh",
            jobTitle: "Founder",
            url: `${SITE_URL}/meet-chris`,
            worksFor: { "@id": `${SITE_URL}/#org` },
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "About", item: `${SITE_URL}/about` },
            ],
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const values = [
    { icon: Mountain, title: "Colorado Roots", desc: "Born and built in Colorado. We live, work, and serve the same communities our clients do." },
    { icon: Target, title: "Outcomes Over Activity", desc: "We obsess over leads, calls, and revenue — not impressions, clicks, or vanity metrics." },
    { icon: Heart, title: "Honest Communication", desc: "We tell you the truth, even when it's hard. Clear reporting, no jargon, no hiding." },
    { icon: Users, title: "Owner-Led Service", desc: "You work directly with senior strategists — never a junior account manager." },
  ];

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Digital Marketing Agency"
        title={
          <>
            A Colorado agency built for{" "}
            <span className="text-gradient-gold">Colorado businesses</span>
          </>
        }
        description="Colorado Web Impressions is a family-owned, faith-based Colorado Springs digital marketing agency helping home service and local businesses across the state generate more qualified leads online."
      />

      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Our Story
            </span>
            <h2 className="mt-3 text-4xl font-extrabold leading-tight text-foreground">
              Built by operators, for operators.
            </h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>
                We started Colorado Web Impressions because we were tired of watching local Colorado
                businesses get burned by big out-of-state agencies selling "marketing" with no
                accountability and no real results.
              </p>
              <p>
                For 25+ years we've been building websites and SEO campaigns for HVAC companies,
                plumbers, roofers, electricians, and contractors across the Front Range. We know
                what works in Colorado Springs. We know what works in Denver. And we know what
                doesn't.
              </p>
              <p>
                We're family-owned and faith-based — and we run the company that way. We partner with a
                select group of Colorado service businesses and treat their marketing like our own,
                because in a small market your reputation is everything.
              </p>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-secondary text-brand-gold">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-section-alt py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Mission &amp; Vision
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              Faith and family first — then great work.
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-background p-7 shadow-card">
              <div className="grid h-11 w-11 place-items-center rounded-lg bg-secondary text-brand-gold">
                <Flag className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-xl font-extrabold text-foreground">Our Mission</h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Serve Colorado small businesses with honest, in-house marketing — guided by faith,
                grounded in family values, and measured by the real results we deliver for the
                owners who trust us.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-background p-7 shadow-card">
              <div className="grid h-11 w-11 place-items-center rounded-lg bg-secondary text-brand-gold">
                <Compass className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-xl font-extrabold text-foreground">Our Vision</h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                To be Colorado's most trusted family-owned, faith-based marketing partner — the team
                local owners recommend by name because we treat their business like our own.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background py-20">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Want to see if we're a fit?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Book a free call. We'll be honest if we're the right partner — and honest if we're not.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-md bg-secondary px-7 py-4 text-base font-semibold text-secondary-foreground transition-smooth hover:bg-foreground"
          >
            Book a Free Strategy Call <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
